import styles from './hoardPricesList.module.css'
import { useEffect, useMemo, useState } from "react"
import { Badge, Button, Table, Column, Data, FieldType, Section, FieldValue, StaticValue, TextField } from "../../../../../features/ui"
import { TableName } from "../../../../../tables"
import { useDataTypesHooks } from "../../../../../features/data-types"
import { Product, ProductName, useProductLoaders } from "../../../../products"
import { useSystemStateHooks } from "../../../../system"
import { HoardPriceCorrection, PricesList } from "../../../state/types"

type Props = {
    pricesList: PricesList
    priceCorrections?: HoardPriceCorrection[]
    collapsable?: boolean
    // Only offered in a financial hoard that can be edited
    onCorrectPrice?: () => void
}

const normalizeText = (text: string) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

export const HoardPricesList = (props: Props) => {
    const { pricesList, priceCorrections = [], collapsable, onCorrectPrice } = props

    const [products, setProducts] = useState<Product[]>([])
    const [searchText, setSearchText] = useState<string>()

    const { formatPrice, formatDateTime } = useDataTypesHooks()
    const { companyUsers } = useSystemStateHooks()

    const productLoaders = useProductLoaders()

    useEffect(() => {
        const productIds = Object.keys(pricesList)
        productLoaders().loadProducts(productIds).then(setProducts)
    }, [])

    const findProductName = (productId: string) => products.find(product => product.id === productId)?.name || ''

    // The corrected products are marked in the list: their previous price is in the corrections below
    const lastCorrections = useMemo(() => new Map(priceCorrections.map(correction => [correction.productId, correction])), [priceCorrections])

    const normalizedSearch = searchText ? normalizeText(searchText) : ''
    const matchesSearch = (productId: string) => !normalizedSearch || normalizeText(findProductName(productId)).includes(normalizedSearch)

    const pricesColumns: Column[] = [
        {
            name: 'productId',
            type: FieldType.TABLE,
            table: TableName.PRODUCTS,
            label: 'Producto',
            loading: products.length === 0,
            render: (value?: FieldValue) => {
                const productId = value as string
                const product = products.find(product => product.id === productId)!
                return <div className={styles.productCell}>
                    <ProductName product={product} />
                    {lastCorrections.has(productId) && (
                        <Badge label="Corregido" kind="secondary" icon="pencil" small />
                    )}
                </div>
            }
        },
        {
            name: 'price',
            type: FieldType.PRICE,
            label: 'Precio'
        }
    ]

    const rows = useMemo(() => Object.entries(pricesList)
        .filter(([productId]) => !!products.find(product => product.id === productId) && matchesSearch(productId))
        .map(([productId, price]) => ({
            productId,
            productName: findProductName(productId),
            price
        }))
        .sort((a, b) => a.productName.localeCompare(b.productName))
    , [pricesList, products, searchText])

    const describeUser = (userId?: string) => {
        const user = companyUsers.find(user => user.id === userId)
        return user?.name || user?.email || '-'
    }

    const correctionColumns: Column[] = [
        {
            name: 'date',
            type: FieldType.DATE,
            label: 'Fecha',
            render: (value?: FieldValue) => formatDateTime(new Date(value as string))
        },
        {
            name: 'productId',
            type: FieldType.TEXT,
            label: 'Producto',
            render: (value?: FieldValue) => findProductName(value as string)
        },
        {
            name: 'previousPrice',
            type: FieldType.PRICE,
            label: 'Precio Anterior',
            render: (value?: FieldValue) => <StaticValue type={FieldType.PRICE} value={value} />
        },
        {
            name: 'newPrice',
            type: FieldType.PRICE,
            label: 'Precio Nuevo',
            render: (value?: FieldValue) => <StaticValue type={FieldType.PRICE} value={value} />
        },
        {
            name: 'returnedBalance',
            type: FieldType.PRICE,
            label: 'Saldo Devuelto',
            render: (value?: FieldValue) => (value as number) > 0 ? <StaticValue type={FieldType.PRICE} value={value} /> : '-'
        },
        {
            name: 'adjustmentNoteCode',
            type: FieldType.TEXT,
            label: 'NC',
            render: (value?: FieldValue) => value ? `#${value}` : '-'
        },
        {
            name: 'userId',
            type: FieldType.TEXT,
            label: 'Usuario',
            render: (value?: FieldValue) => describeUser(value as string)
        }
    ]

    // The search also filters the corrections: only the ones of the products found
    const correctionRows = priceCorrections
        .map((correction, index) => ({ ...correction, id: index.toString() }))
        .filter(correction => matchesSearch(correction.productId))
        .reverse() as unknown as Data[]

    return (
        <Section id="hoardPricesList" collapsable={collapsable}><>
            <div className={styles.header}>
                <div className={styles.search}>
                    <TextField
                        id="hoard-prices-list-search"
                        icon="search"
                        freeValue
                        optional
                        onChange={setSearchText}
                    />
                </div>
                {onCorrectPrice && (
                    <Button
                        label="Corregir Precio"
                        icon="pencil-square"
                        kind="tertiary"
                        onClick={onCorrectPrice}
                    />
                )}
            </div>
            <Table
                columns={pricesColumns}
                rows={rows}
                rowId="productId"
                pageSize={20}
                readOnly
            />
            {correctionRows.length > 0 && (
                <div className={styles.corrections}>
                    <h6>Correcciones de precio</h6>
                    <Table
                        columns={correctionColumns}
                        rows={correctionRows}
                        rowId="id"
                        alignRight={false}
                        readOnly
                    />
                </div>
            )}
        </></Section>
    )
}
